/**
 * SSE consumer for the chat clerk's /ask endpoint.
 *
 * The Worker streams these events:
 *   event: text              → {"delta": "..."}
 *   event: tool_call         → {"name": "...", "input": {...}}          (tool started)
 *   event: tool_result       → {"name": "...", "ok": true, "elapsed_ms": N, "summary": "..."}
 *   event: pipeline_progress → {"pipeline_id", "run_id", "status", "seq", "nodes": [...]}
 *   event: pipeline_run      → {"pipeline_id": "...", "outcome": {...}}
 *   event: form              → LetterFormSpec (the all-in-one letter intake card;
 *                              submit sends `form_payload` on the next ask body)
 *   event: final             → {"answer": "...", "citations": [...], ...}
 *   event: error             → {"message": "..."}
 */

export interface PipelineArtifact {
  name: string;
  url?: string;
  kind?: string;       // "gdoc" for a Google Doc link; otherwise a docx download
  pdf_url?: string;
  bytes?: number;
}

export interface PipelineRun {
  pipeline_id: string;
  status: string;
  artifacts: PipelineArtifact[];
  error?: string;
}

export interface ToolResult {
  name: string;
  ok: boolean;
  elapsed_ms: number;
  summary?: string;
}

export interface PipelineProgressNode {
  id: string;
  kind?: string;
  label?: string;
  status: string;   // pending | running | done | error
}

/** A live per-node snapshot of a run_pipeline still executing on the Lambda. */
export interface PipelineProgress {
  pipeline_id: string;
  run_id: string;
  status: string;
  seq: number;
  nodes: PipelineProgressNode[];
}

/** One field on the all-in-one letter intake form (mirrors the Worker's
 *  chat/letter_form.ts spec). */
export interface LetterFormField {
  name: string;
  required: boolean;
  kind: "string" | "number";
  description?: string;
  /** Pre-filled from the conversation by the clerk. */
  value?: unknown;
}

export interface LetterFormType {
  id: string;        // pipeline id the submission runs
  name: string;
  fields: LetterFormField[];
}

export interface LetterFormSpec {
  types: LetterFormType[];
  selected?: string;
  /** HR-directory auto-population is available on this session. */
  prefill: boolean;
  /** Client-stamped arrival time — keys the card so a fresh form event
   *  re-seeds it while edits within one form survive re-renders. */
  received_at?: number;
}

/** Submitted form values — sent as `form_payload` on the ask body. */
export interface LetterFormPayload {
  letter_type: string;
  payload: Record<string, unknown>;
}

export interface SseHandlers {
  onText?: (delta: string) => void;
  onToolCall?: (call: { name: string; input: unknown }) => void;
  onToolResult?: (result: ToolResult) => void;
  onPipelineProgress?: (progress: PipelineProgress) => void;
  onPipelineRun?: (run: PipelineRun) => void;
  onForm?: (form: LetterFormSpec) => void;
  onFinal?: (final: SseFinal) => void;
  onError?: (msg: string) => void;
}

export interface SseFinal {
  answer: string;
  citations: string[];
  tool_calls: number;
  tokens_in: number;
  tokens_out: number;
}


/**
 * POSTs to `url` with body { question }, parses the SSE stream, fires
 * handlers as events arrive. Returns a promise that resolves on final
 * event or rejects on error.
 *
 * `fetch` is used directly (not EventSource) because EventSource is
 * GET-only and we need POST for the question body.
 */
export async function ask(
  url: string,
  question: string,
  handlers: SseHandlers,
  signal?: AbortSignal,
  files: string[] = [],
  formPayload?: LetterFormPayload,
): Promise<SseFinal> {
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      question, files,
      ...(formPayload ? { form_payload: formPayload } : {}),
    }),
    signal,
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`ask ${resp.status}: ${text.slice(0, 300)}`);
  }
  if (!resp.body) {
    throw new Error("ask response has no body");
  }

  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let final: SseFinal | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });

    const events = buf.split("\n\n");
    buf = events.pop() || "";

    for (const ev of events) {
      let name = "";
      let data = "";
      for (const line of ev.split("\n")) {
        if (line.startsWith("event:")) name = line.slice(6).trim();
        else if (line.startsWith("data:")) data = line.slice(5).trim();
      }
      if (!data) continue;
      let payload: unknown;
      try { payload = JSON.parse(data); } catch { continue; }

      if (name === "text" && handlers.onText) {
        handlers.onText((payload as any).delta ?? "");
      } else if (name === "tool_call" && handlers.onToolCall) {
        handlers.onToolCall(payload as { name: string; input: unknown });
      } else if (name === "tool_result" && handlers.onToolResult) {
        handlers.onToolResult(payload as ToolResult);
      } else if (name === "pipeline_progress" && handlers.onPipelineProgress) {
        handlers.onPipelineProgress(payload as PipelineProgress);
      } else if (name === "pipeline_run" && handlers.onPipelineRun) {
        const p = payload as { pipeline_id?: string; outcome?: any };
        const o = p.outcome || {};
        handlers.onPipelineRun({
          pipeline_id: p.pipeline_id || "",
          status: o.status || (o.error ? "error" : "done"),
          artifacts: Array.isArray(o.artifacts) ? o.artifacts : [],
          error: o.error,
        });
      } else if (name === "form" && handlers.onForm) {
        handlers.onForm(payload as LetterFormSpec);
      } else if (name === "final") {
        final = payload as SseFinal;
        handlers.onFinal?.(final);
      } else if (name === "error") {
        const msg = (payload as any).message ?? "unknown error";
        handlers.onError?.(msg);
        throw new Error(msg);
      }
    }
  }

  if (!final) {
    throw new Error("ask stream ended without final event");
  }
  return final;
}
